import React, { useState } from 'react';
import { Alert, App, Button, Modal, Typography } from 'antd';
import { CheckOutlined, CopyOutlined, DownloadOutlined } from '@ant-design/icons';

const { Paragraph, Text } = Typography;

function maskActivationCode(code) {
  if (!code) return 'Unavailable';
  if (code.length <= 4) return code;
  return `${'*'.repeat(code.length - 4)}${code.slice(-4)}`;
}

export default function StarterKitIssuanceModal({
  open,
  entitlement,
  isRegenerateFlow = false,
  downloading = false,
  onCancel,
  onDownload,
}) {
  const { message } = App.useApp();
  const activationCode = entitlement?.activation_code || '';
  const projectName = entitlement?.project_name || entitlement?.project_id || 'Selected project';
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!activationCode) return;
    try {
      await navigator.clipboard.writeText(activationCode);
      setCopied(true);
      message.success('Activation code copied');
      window.setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      message.error('Failed to copy activation code');
    }
  };

  return (
    <Modal
      open={open}
      title={(
        <span>
          Project:
          {' '}
          <Text strong style={{ color: '#0f172a' }}>{projectName}</Text>
        </span>
      )}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={downloading}>
          Close
        </Button>,
        <Button
          key="download"
          type="primary"
          icon={<DownloadOutlined />}
          onClick={onDownload}
          loading={downloading}
        >
          Starter Kit
        </Button>,
      ]}
      centered
      destroyOnClose
    >
      <Paragraph style={{ marginTop: 0, marginBottom: 12, color: '#334155' }}>
        Your activation code is below. Copy it now — you will not see the full code again
        after you close this window.
      </Paragraph>
      {isRegenerateFlow ? (
        <Alert
          type="warning"
          showIcon
          message="Previous Kit and token revoked"
          description="Generating again revokes the previous activation code and Kit. Older downloads will no longer activate or log in."
          style={{ marginBottom: 16 }}
        />
      ) : null}
      <Alert
        type="info"
        showIcon
        message="Keep this activation code safe"
        description="The downloaded app will ask for it the first time it is launched."
        style={{ marginBottom: 16 }}
      />
      <Text strong style={{ display: 'block', marginBottom: 8, color: '#0f172a' }}>
        Project activation code (
        {projectName}
        )
      </Text>
      <div
        style={{
          padding: '8px 12px',
          borderRadius: 8,
          border: '1px solid #CBD5E1',
          background: '#F1F5F9',
          marginBottom: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <Text
          style={{
            fontSize: 16,
            letterSpacing: '0.06em',
            color: '#64748B',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          }}
        >
          {maskActivationCode(activationCode)}
        </Text>
        <Button
          size="small"
          icon={copied ? <CheckOutlined /> : <CopyOutlined />}
          onClick={handleCopy}
          disabled={!activationCode}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <Paragraph style={{ color: '#475569', marginBottom: 0 }}>
        When you are ready, click
        {' '}
        <Text strong>Starter Kit</Text>
        {' '}
        to download the app. If you close this window before downloading, this activation
        code will be cancelled.
      </Paragraph>
    </Modal>
  );
}
